/**
 * The scene layout (D210): {@link MapGraph} → the rooms as boxes in a real
 * three-dimensional space, ready for any renderer that can draw a box.
 *
 * @remarks
 * The hologram (D169) is a picture: a fixed-angle projection computed as
 * polygons, which is why it is deterministic, golden-pinned and readable by
 * a screen reader. The scene is the same rooms with the angle left to the
 * player — dragged to rotate, wheeled to zoom — and that needs a renderer
 * with a camera. This module is everything BEFORE the camera: where each
 * box stands, how big it is, which boxes a beam joins, where the floors
 * lie, and how far the whole thing reaches, all in world units, all
 * derived from the graph and nothing else. A renderer (`player-scene`) only
 * ever draws this record; it computes no geometry of its own. That keeps
 * the isomorphic boundary where it is — no three.js in the engine, no
 * geometry in the browser package — and keeps the scene as testable as the
 * hologram: same graph, same boxes, byte for byte.
 *
 * Units and axes: one grid cell is one world unit; a room's footprint is the
 * styles' shared table (`map-footprints.ts`), so the plan, the hologram and
 * the scene agree about the shape of a corridor. The world is y-up: grid x
 * runs along world x, grid y (south on the plan) runs along world z toward
 * the viewer's default position, and each floor sits {@link FLOOR_HEIGHT}
 * above the one below. A box's `y` is its BASE — the floor it stands on —
 * so a renderer stands it up rather than centers it.
 *
 * @packageDocumentation
 */

import type { MapRoomKind } from '@orpheus/content-schema';

import type { MapGraph, MapNodeKnowledge } from './map-graph.js';

import { UNKNOWN_LABEL } from './derive-map-graph.js';
import { floorLabelOf, footprintOf, legendOf, type MapLegendKey } from './map-footprints.js';
import { describeGraph } from './map-svg-layout.js';

/** How far up each floor sits above the one below, in world units (cells). */
export const FLOOR_HEIGHT = 1.4;
/** How far a floor plate reaches past the outermost cell on it, in cells. */
const FLOOR_MARGIN = 0.62;
/** How tall each kind of room stands, in world units. */
const HEIGHTS: Readonly<Record<MapRoomKind, number>> = {
	chamber: 0.5,
	corridor: 0.14,
	room: 0.32,
	shaft: 0.72,
};

/** A point in the scene: x across, y up, z toward the viewer's default position. */
export type ScenePoint = readonly [x: number, y: number, z: number];

/** One room as a box standing on its floor. */
export interface MapSceneBox {
	/** How tall the box stands above its base. */
	readonly height: number;
	readonly isActive: boolean;
	readonly isCheckpoint: boolean;
	readonly key: string;
	readonly kind: MapRoomKind;
	readonly knowledge: MapNodeKnowledge;
	/** The room's name, with the active room's marker; the unknown label past a known exit. */
	readonly label: string;
	readonly level: number;
	/** The footprint's full extent along x. */
	readonly width: number;
	/** The footprint's full extent along z. */
	readonly depth: number;
	/** The box's center along x. */
	readonly x: number;
	/** The box's BASE — the floor it stands on. */
	readonly y: number;
	/** The box's center along z. */
	readonly z: number;
}

/** One link on the floor plane between two cells, at floor height. */
export interface MapSceneBeam {
	readonly from: ScenePoint;
	readonly level: number;
	readonly to: ScenePoint;
}

/** One lift: the vertical between a cell on one floor and the same cell on another. */
export interface MapSceneLift {
	readonly from: ScenePoint;
	readonly to: ScenePoint;
}

/** One floor plate: the rectangle under every box on a level, and its name. */
export interface MapSceneFloor {
	readonly label: string;
	readonly level: number;
	readonly maxX: number;
	readonly maxZ: number;
	readonly minX: number;
	readonly minZ: number;
	/** The plate's height in the world. */
	readonly y: number;
}

/** A complete scene, ready for any renderer that draws boxes and lines. */
export interface MapSceneLayout {
	readonly beams: readonly MapSceneBeam[];
	readonly boxes: readonly MapSceneBox[];
	/** The point a camera orbits around: the middle of everything drawn. */
	readonly center: ScenePoint;
	/** Long-form description for the figure's accessible name. */
	readonly description: string;
	readonly floors: readonly MapSceneFloor[];
	readonly legend: readonly MapLegendKey[];
	readonly lifts: readonly MapSceneLift[];
	/** How far the drawing reaches from `center`, so a camera can frame it. */
	readonly radius: number;
}

const round = (value: number): number => Math.round(value * 1000) / 1000;

/** A cell on a floor, in the world. */
const place = (x: number, y: number, level: number, height = 0): ScenePoint => [round(x), round(level * FLOOR_HEIGHT + height), round(y)];

/**
 * Lay one derived map out as a scene.
 *
 * @param graph - The derived map, derived with levels
 * @returns The positioned record
 */
export const buildMapSceneLayout = (graph: MapGraph): MapSceneLayout => {
	const levels = [...new Set(graph.nodes.map((node) => node.level))].sort((left, right) => left - right);

	const floors = levels.map((level): MapSceneFloor => {
		const onLevel = graph.nodes.filter((node) => node.level === level);

		return {
			label: floorLabelOf(level),
			level,
			maxX: round(Math.max(...onLevel.map((node) => node.x)) + FLOOR_MARGIN),
			maxZ: round(Math.max(...onLevel.map((node) => node.y)) + FLOOR_MARGIN),
			minX: round(Math.min(...onLevel.map((node) => node.x)) - FLOOR_MARGIN),
			minZ: round(Math.min(...onLevel.map((node) => node.y)) - FLOOR_MARGIN),
			y: round(level * FLOOR_HEIGHT),
		};
	});

	const boxes = graph.nodes.map((node): MapSceneBox => {
		const { halfX, halfY } = footprintOf(graph, node);
		const label = node.name ?? UNKNOWN_LABEL;

		return {
			depth: round(halfY * 2),
			height: HEIGHTS[node.kind],
			isActive: node.isActive,
			isCheckpoint: node.isCheckpoint,
			key: `${String(node.x)},${String(node.y)},${String(node.level)}`,
			kind: node.kind,
			knowledge: node.knowledge,
			label: node.isActive ? `▸ ${label}` : label,
			level: node.level,
			width: round(halfX * 2),
			x: round(node.x),
			y: round(node.level * FLOOR_HEIGHT),
			z: round(node.y),
		};
	});

	const minX = Math.min(...floors.map((floor) => floor.minX));
	const maxX = Math.max(...floors.map((floor) => floor.maxX));
	const minZ = Math.min(...floors.map((floor) => floor.minZ));
	const maxZ = Math.max(...floors.map((floor) => floor.maxZ));
	const minY = Math.min(...floors.map((floor) => floor.y));
	const maxY = Math.max(...boxes.map((box) => box.y + box.height));
	const center: ScenePoint = [round((minX + maxX) / 2), round((minY + maxY) / 2), round((minZ + maxZ) / 2)];

	return {
		beams: graph.edges.map((edge) => ({ from: place(edge.fromX, edge.fromY, edge.level), level: edge.level, to: place(edge.toX, edge.toY, edge.level) })),
		boxes,
		center,
		description: `${describeGraph(graph)}${levels.length > 1 ? ` Across ${String(levels.length)} floors.` : ''}`,
		floors,
		legend: legendOf(graph),
		lifts: graph.lifts.map((lift) => ({ from: place(lift.x, lift.y, lift.fromLevel), to: place(lift.x, lift.y, lift.toLevel) })),
		// Half the diagonal of the box around everything: a camera at this
		// distance times a fixed factor sees the whole drawing at any angle.
		radius: round(Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2),
	};
};
